/**
 * Tier models: which model each lane serves for each tier, picked from the
 * lane's price list. Edits stage locally (dirty cells turn amber), then one
 * reason and one Save issue a POST per changed cell. "reset" releases an
 * Admin binding back to the built-in default.
 */

import { useMemo, useState } from "react";
import { Button } from "../../../ui/button.js";
import { Input } from "../../../ui/input.js";
import { NativeSelect } from "../../../ui/native-select.js";
import type { Lane, Tier, TierModelsResponse } from "./types.js";
import {
  EMPTY_CATALOG_LABEL,
  LANE_NAMES,
  PIN_ONLY_COPY,
  hostingBadge,
  ordinal,
  priceLabel,
} from "./format.js";
import { JSON_HEADERS, describeError, responseError } from "./http.js";
import {
  PRINT_CARD_CLASS,
  PRINT_CARD_STYLE,
  TABLE_HEADER_CLASS,
  TABLE_HEADER_STYLE,
} from "./styles.js";

interface TierModelsCardProps {
  data: Pick<TierModelsResponse, "policy" | "providers" | "tiers" | "catalog" | "effective">;
  onChanged: () => Promise<void>;
  onError: (message: string | null) => void;
}

function cellKey(lane: Lane, tier: Tier): string {
  return `${lane}:${tier}`;
}

export function TierModelsCard({ data, onChanged, onError }: TierModelsCardProps) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState<string | null>(null);

  // Columns follow the unrestricted lane order; any lane the policy omits
  // trails so it stays editable.
  const columns = useMemo<Lane[]>(
    () => [
      ...data.policy.unrestricted,
      ...data.providers.filter((p) => !data.policy.unrestricted.includes(p)),
    ],
    [data.policy.unrestricted, data.providers],
  );

  const changes = useMemo(() => {
    const out: Array<{ lane: Lane; tier: Tier; model: string }> = [];
    for (const lane of columns) {
      for (const tier of data.tiers) {
        const draft = drafts[cellKey(lane, tier)];
        if (draft !== undefined && draft !== data.effective[lane]?.[tier]?.model) {
          out.push({ lane, tier, model: draft });
        }
      }
    }
    return out;
  }, [columns, data.tiers, data.effective, drafts]);

  async function save() {
    if (changes.length === 0 || !reason.trim()) return;
    setSaving(true);
    onError(null);
    const saved: string[] = [];
    try {
      for (const change of changes) {
        const res = await fetch("/api/admin/tier-models", {
          method: "POST",
          headers: JSON_HEADERS,
          body: JSON.stringify({
            provider: change.lane,
            tier: change.tier,
            model: change.model,
            reason: reason.trim(),
          }),
        });
        if (!res.ok) {
          onError(
            `${LANE_NAMES[change.lane]} ${change.tier}: ${await responseError(res)}`,
          );
          break;
        }
        saved.push(cellKey(change.lane, change.tier));
      }
    } catch (err) {
      onError(describeError(err, "Unable to save tier models"));
    } finally {
      if (saved.length > 0) {
        setDrafts((d) => {
          const next = { ...d };
          for (const key of saved) delete next[key];
          return next;
        });
        if (saved.length === changes.length) setReason("");
        await onChanged();
      }
      setSaving(false);
    }
  }

  async function reset(lane: Lane, tier: Tier, bindingId: string) {
    const key = cellKey(lane, tier);
    setResetting(key);
    onError(null);
    try {
      const res = await fetch(
        `/api/admin/tier-models/${encodeURIComponent(bindingId)}/release`,
        { method: "POST" },
      );
      if (!res.ok) {
        onError(await responseError(res));
        return;
      }
      setDrafts((d) => {
        const next = { ...d };
        delete next[key];
        return next;
      });
      await onChanged();
    } catch (err) {
      onError(describeError(err, "Unable to reset the tier model"));
    } finally {
      setResetting(null);
    }
  }

  const n = changes.length;
  const saveLabel = n === 0 ? "No changes" : `Save ${n} ${n === 1 ? "change" : "changes"}`;

  return (
    <section aria-label="Tier models" className={PRINT_CARD_CLASS} style={PRINT_CARD_STYLE}>
      <h3 className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
        Tier models
      </h3>
      <p className="text-xs mb-3" style={{ color: "var(--text-secondary)" }}>
        What each lane serves for each tier, for every workspace without a pin. Picks come from
        the lane's price list, per million input and output tokens. Live within a minute.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-[13px]" style={{ color: "var(--text-primary)" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border-subtle)" }}>
              <th className={TABLE_HEADER_CLASS} style={TABLE_HEADER_STYLE}>
                Tier
              </th>
              {columns.map((lane, index) => (
                <th key={lane} className={TABLE_HEADER_CLASS} style={TABLE_HEADER_STYLE}>
                  <div>{LANE_NAMES[lane]}</div>
                  <div className="normal-case tracking-normal" style={{ color: "var(--text-tertiary)" }}>
                    {data.policy.unrestricted.includes(lane)
                      ? `${ordinal(index + 1)} · ${hostingBadge(lane)}`
                      : `pin only · ${hostingBadge(lane)}`}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.tiers.map((tier) => (
              <tr key={tier} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                <td className="px-3 py-2 align-top font-mono font-medium">{tier}</td>
                {columns.map((lane) => {
                  const key = cellKey(lane, tier);
                  const cell = data.effective[lane]?.[tier];
                  const options = data.catalog[lane] ?? [];
                  const current = cell?.model ?? "";
                  const value = drafts[key] ?? current;
                  const dirty = value !== current;
                  const offCatalog = current !== "" && !options.some((o) => o.id === current);
                  return (
                    <td key={key} className="px-3 py-2 align-top">
                      <NativeSelect
                        aria-label={`Model for ${LANE_NAMES[lane]} ${tier}`}
                        selectSize="sm"
                        value={value}
                        disabled={saving}
                        onChange={(e) =>
                          setDrafts((d) => {
                            const next = { ...d };
                            if (e.target.value === current) delete next[key];
                            else next[key] = e.target.value;
                            return next;
                          })
                        }
                        className={dirty ? "border-[var(--warning)] text-xs" : "text-xs"}
                        wrapperClassName="w-full min-w-[200px]"
                      >
                        {offCatalog && (
                          <option value={current} disabled>
                            {`Not on the price list: ${current}`}
                          </option>
                        )}
                        {options.length === 0 && (
                          <option value={offCatalog ? "" : current} disabled>
                            {EMPTY_CATALOG_LABEL}
                          </option>
                        )}
                        {options.map((o) => (
                          <option key={o.id} value={o.id}>
                            {priceLabel(o)}
                          </option>
                        ))}
                      </NativeSelect>
                      <div
                        className="mt-1 truncate text-[10.5px]"
                        style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}
                        title={value}
                      >
                        {value}
                      </div>
                      <div
                        className="mt-0.5 text-[10px] uppercase tracking-wider"
                        style={{ fontFamily: "var(--font-mono)" }}
                      >
                        {dirty ? (
                          <span style={{ color: "var(--warning)" }}>Changed · saves as Admin</span>
                        ) : cell?.source === "admin" && cell.bindingId ? (
                          <span style={{ color: "var(--accent)" }}>
                            Admin ·{" "}
                            <button
                              type="button"
                              className="uppercase underline disabled:opacity-50"
                              style={{ color: "var(--accent)" }}
                              aria-label={`Reset ${LANE_NAMES[lane]} ${tier} to built-in`}
                              disabled={resetting !== null || saving}
                              onClick={() => {
                                if (cell.bindingId) void reset(lane, tier, cell.bindingId);
                              }}
                            >
                              reset
                            </button>
                          </span>
                        ) : (
                          <span style={{ color: "var(--text-tertiary)" }}>Built-in</span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label
          className="flex min-w-[260px] flex-1 flex-col gap-1 text-xs"
          style={{ color: "var(--text-secondary)" }}
        >
          Reason for change (required to save)
          <Input
            aria-label="Reason for change"
            inputSize="sm"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="who asked, and why"
          />
        </label>
        {n > 0 && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => setDrafts({})}
            disabled={saving}
          >
            Discard
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          onClick={() => void save()}
          disabled={n === 0 || !reason.trim() || saving}
          loading={saving}
          title={n > 0 && !reason.trim() ? "Enter a reason for change first" : undefined}
        >
          {saveLabel}
        </Button>
      </div>
      <p className="mt-2 text-xs" style={{ color: "var(--text-tertiary)" }}>
        Fireworks US offers the router models Fireworks publishes on its US lane, priced at 1.5×
        the global row. Anthropic US sends every request with US-only inference, at 1.1×.
      </p>
      {(data.policy.pinOnly?.length ?? 0) > 0 && (
        <p className="mt-1 text-xs" style={{ color: "var(--text-tertiary)" }}>
          {PIN_ONLY_COPY}
        </p>
      )}
    </section>
  );
}
